/**
 * @file tag-select.component.tsx
 * @description Многофункциональный компонент выбора тегов с поддержкой асинхронной загрузки данных через API, 
 * мультивыбором, добавлением новых тегов, различными режимами отображения (default/clear) и интегрированным поиском.
 * Поддерживает валидацию, кастомные плейсхолдеры и состояния disabled/required. Работает с динамическим типизированным каталогом.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, axios, @api/actions/CatalogService, @src/types/TBaseObjectDto, @components/ui/base/text, @consta/icons/IconClose, classnames]
 * @related-files [./components/tag-list.component.tsx, @components/ui/base/text/text.component.tsx]
 */

import { useEffect, useRef, useState } from 'react';
import { CatalogService } from '@api/actions';
import { TBaseObjectDto } from '@src/types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Text } from '@components/ui/base/text';
import { IconClose } from '@consta/icons/IconClose';
import { TagList } from './components';
import cn from 'classnames';

/**
 * Props для компонента выбора тегов
 */
interface ITagSelectProps {
    /** Выбранные теги */
    value?: TBaseObjectDto[];
    /** Обработчик изменения выбранных тегов */
    onChange: (newItems: TBaseObjectDto[]) => void;
    /** Разрешить множественный выбор */
    multiple?: boolean;
    /** Метка поля */
    label?: string;
    /** Тип каталога для загрузки тегов */
    type: string;
    /** Плейсхолдер для поля поиска */
    placeholder?: string;
    /** Заблокировано ли поле */
    isDisabled?: boolean;
    /** Обязательно ли поле */
    isRequired?: boolean;
    view?: 'default' | 'clear';
}

export const TagSelect = ({
    value,
    onChange,
    label,
    type,
    placeholder,
    isDisabled = false,
    isRequired = false,
    view = 'default'
}: ITagSelectProps) => {
    const [items, setItems] = useState<TBaseObjectDto[] | null>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const [isShowSelect, setIsShowSelect] = useState<boolean>(false);

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        setItems(null);
        const response: AxiosResponse | AxiosError = await CatalogService.getList(type);

        if (axios.isAxiosError(response) || !response.data.objects?.length) {
            console.log('errors', response);
            return;
        }

        setItems(response.data.objects);
    };

    return (
        <div className="tag-select-component">
            {label && (
                <div className="flex">
                    <Text size="s" view="secondary" className={`whitespace-nowrap dictionary__label mb-[0.5283vmin]`}>
                        {label}
                    </Text>

                    {isRequired && <span className="FieldLabel-Star">*</span>}
                </div>
            )}

            <div
                ref={inputRef}
                className={cn('dictionary__input flex flex-row flex-wrap bg-white min-h-[30px]', {
                    'border rounded border-[#00426947] hover:border-[#00426985]': view === 'default',
                    'h-full dictionary__input_clear': view !== 'default'
                })}
                onClick={() => setIsShowSelect(true)}
            >
                {value?.length
                    ? value.map((item: TBaseObjectDto, key: number) => (
                          <div
                              key={key}
                              className={cn(
                                  'input_element flex gap-[4px] rounded-md items-center hover:bg-[#F1F5F6] px-[9px] h-[28px]',
                                  {
                                      'cursor-pointer': !isDisabled
                                  }
                              )}
                          >
                              <div
                                  className="input_color h-[14px] w-[14px] rounded-full aspect-square"
                                  style={{ background: item.color }}
                              />

                              <Text size="s">{item.name}</Text>

                              <IconClose
                                  className="hover:text-link-hover"
                                  size="xs"
                                  onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onChange(value.filter((tag: TBaseObjectDto) => tag.$item.id !== item.$item.id));
                                  }}
                              />
                          </div>
                      ))
                    : placeholder && (
                          <div className="w-full h-auto flex items-center">
                              <Text size="m" view="secondary" className="px-[8px]">
                                  {placeholder}
                              </Text>
                          </div>
                      )}
            </div>

            {isShowSelect && items && (
                <TagList
                    anchorRef={inputRef}
                    hideHandler={() => setIsShowSelect(false)}
                    items={items}
                    onChange={(newValue: TBaseObjectDto[]) => {
                        setItems(newValue);
                        //TODO: naming

                        if (!value?.length) {
                            return;
                        }

                        const _newValue = value?.map((val: TBaseObjectDto) => {
                            const _item = newValue?.find((item: TBaseObjectDto) => val.$item.id === item.$item.id);

                            if (_item && (_item.name !== val.name || val.color !== _item.color)) {
                                return { ...val, $item: _item.$item, name: _item.name, color: _item.color };
                            }

                            return val;
                        });
                        onChange(_newValue);
                    }}
                    onItemClick={(clickedItem: TBaseObjectDto) => {
                        if (value?.find((item: TBaseObjectDto) => item.$item?.id === clickedItem.id)) {
                            return;
                        }

                        const newValue = { $item: clickedItem, name: clickedItem.name, color: clickedItem.color };
                        onChange([...(value || []), newValue]);
                    }}
                    type={type}
                />
            )}
        </div>
    );
};
